import { useMemo } from "react"

import { logger } from "../logger"

export type FieldMapping<TSource, TDestination> = {
  [K in keyof TDestination]: (source: TSource) => TDestination[K]
}

export type MapFieldsResult<TDestination> = {
  data: TDestination
  errors?: Partial<Record<keyof TDestination, FieldMappingError>>
}

export class FieldMappingError extends Error {
  constructor(field: string, cause: Error) {
    super(`Error mapping field '${field}'`, { cause })
    this.name = new.target.name
    Error.captureStackTrace?.(this, new.target)
  }
}

const queryAdapterLogger = logger.withScope("useQueryAdapter")

/**
 * Maps fields from a generic source to a fully typed destination model. The
 * caller is responsible for throwing if a required field fails to map, and
 * handling any propogated errors. We assume this risk to avoid pervasive checks
 * downstream due to generated proto3 optional response types.
 */
export function mapFields<TSource, TDestination>(
  source: TSource,
  mapping: FieldMapping<TSource, TDestination>
): MapFieldsResult<TDestination> {
  // Assert TDestination to build incrementally
  const destination = {} as TDestination
  const errors: Partial<Record<keyof TDestination, FieldMappingError>> = {}

  for (const field in mapping) {
    try {
      destination[field] = mapping[field](source)
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err))
      errors[field] = new FieldMappingError(field, error)
      queryAdapterLogger.error(errors[field].message, { field }, errors[field])
    }
  }

  return {
    data: destination,
    ...(Object.keys(errors).length > 0 && { errors }),
  }
}

export function mapFieldsArray<TSource, TDestination>(
  source: TSource[],
  mapping: FieldMapping<TSource, TDestination>
): TDestination[] {
  const results: TDestination[] = []
  for (const item of source) {
    const { data, errors } = mapFields(item, mapping)
    if (errors) {
      continue
    }
    results.push(data)
  }
  return results
}

export type UseQueryAdapterProps<TData, TAdapted> = {
  query: {
    // Common accessor property on all RTK or Tanstack Query responses
    // Extract nested payloads from `data` if needed (e.g., search `results`)
    data?: TData
  }
  //  Simple 1:1 (likely leveraging mapFields)
  adapter: (data: TData) => TAdapted
  defaultValue: TAdapted
}

export type UseQueryAdapterResult<TAdapted> = {
  data: TAdapted
  error?: Error
}

export function useQueryAdapter<TData, TAdapted>({
  query,
  adapter,
  defaultValue,
}: UseQueryAdapterProps<TData, TAdapted>): UseQueryAdapterResult<TAdapted> {
  return useMemo(() => {
    // Existence of `query.data` is the sole signal
    // (i.e. ignoring query state)
    if (query.data === undefined) return { data: defaultValue }

    try {
      return { data: adapter(query.data) }
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error("Adapter error in useQueryAdapter", {}, error)
      return { data: defaultValue, error }
    }
  }, [query, adapter, defaultValue])
}
